import { useEffect, useState } from 'react';
import {
  TYPE_LABELS,
  canDelete,
  groupByType,
  isLiability,
  netPositionPaise,
  outstandingPaise,
  type Account,
  type AccountType,
} from '../../core/accounts';
import { formatDateKey, todayKey } from '../../core/dates';
import { formatPaise, parseAmount } from '../../core/money';
import {
  AccountError,
  createAccount,
  deleteAccount,
  seedAccounts,
  setArchived,
  updateAccount,
  watchAccounts,
} from '../../data/accounts';
import type { UserRecord } from '../../data/auth';

interface Props {
  record: UserRecord;
}

export function AccountsScreen({ record }: Props) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => watchAccounts(setAccounts), []);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      // Surface the underlying message, not a generic one. A permission failure and a
      // network failure need different responses from the user, and "that did not
      // work" tells them neither.
      const detail =
        cause instanceof AccountError
          ? cause.message
          : cause instanceof Error
            ? `${cause.message}`
            : 'That did not work.';
      setError(detail);
      if (!(cause instanceof AccountError)) console.error('account action failed', cause);
    } finally {
      setBusy(false);
    }
  }

  if (accounts === null) return <p className="muted">Loading accounts…</p>;

  if (accounts.length === 0) {
    return (
      <>
        <h1>Accounts</h1>
        <div className="placeholder">
          <p>No accounts yet.</p>
          <p className="small">
            Seeds 32 accounts from the workbook — 18 bank and cash totalling
            ₹11,96,983.23, 7 cards at ₹1,93,924.31 outstanding, 6 savings and
            investment, and the car loan. Balances are as at 1&nbsp;Aug&nbsp;2026.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void run(() => seedAccounts(record.uid))}
          >
            {busy ? 'Seeding…' : 'Seed accounts from workbook'}
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      </>
    );
  }

  const visible = accounts.filter((a) => showArchived || !a.archived);
  const net = netPositionPaise(accounts.filter((a) => !a.archived).map((a) => a.openingBalancePaise));
  const archivedCount = accounts.filter((a) => a.archived).length;

  return (
    <>
      <div className="head-row">
        <h1>Accounts</h1>
        <button type="button" className="btn" onClick={() => setAdding(true)}>
          Add account
        </button>
      </div>

      <p className="muted">
        {accounts.length} accounts · net position <strong>{formatPaise(net)}</strong>
        {archivedCount > 0 && (
          <>
            {' · '}
            <button
              type="button"
              className="btn-link"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? 'Hide' : 'Show'} {archivedCount} archived
            </button>
          </>
        )}
      </p>

      {error && <p className="error">{error}</p>}

      {adding && (
        <AccountForm
          onCancel={() => setAdding(false)}
          onSave={(input) =>
            run(async () => {
              await createAccount(input, record.uid);
              setAdding(false);
            })
          }
        />
      )}

      {editing && (
        <AccountForm
          account={editing}
          onCancel={() => setEditing(null)}
          onSave={(input) =>
            run(async () => {
              await updateAccount(editing, input, record.uid);
              setEditing(null);
            })
          }
        />
      )}

      {groupByType(visible).map(([type, list]) => (
        <section key={type} className="panel">
          <h2>
            {TYPE_LABELS[type]} <span className="muted small">({list.length})</span>
          </h2>
          <table className="table">
            <tbody>
              {list.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  busy={busy}
                  onEdit={() => setEditing(account)}
                  onArchive={() =>
                    run(() => setArchived(account, !account.archived, record.uid))
                  }
                  onDelete={() => run(() => deleteAccount(account, record.uid))}
                />
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </>
  );
}

interface RowProps {
  account: Account;
  busy: boolean;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

function AccountRow({ account, busy, onEdit, onArchive, onDelete }: RowProps) {
  const liability = isLiability(account.type);
  const amount = liability
    ? outstandingPaise(account.openingBalancePaise)
    : account.openingBalancePaise;

  return (
    <tr className={account.archived ? 'archived' : undefined}>
      <td>
        {account.name}
        {account.archived && <span className="badge">archived</span>}
        <div className="muted tiny">
          since {formatDateKey(account.openingBalanceDate, 'dd MMM yyyy')}
          {account.transactionCount > 0 && ` · ${account.transactionCount} transactions`}
        </div>
        {account.note && <div className="muted tiny note">{account.note}</div>}
      </td>
      <td className="num">
        {formatPaise(amount)}
        {liability && <div className="muted tiny">outstanding</div>}
      </td>
      <td className="actions">
        <button type="button" className="btn-link" onClick={onEdit} disabled={busy}>
          Edit
        </button>
        <button type="button" className="btn-link" onClick={onArchive} disabled={busy}>
          {account.archived ? 'Restore' : 'Archive'}
        </button>
        {/* FR-ACC-03: absent, not disabled. An account with transactions will never
            become deletable, so a greyed-out control would only invite the question. */}
        {canDelete(account) && (
          <button type="button" className="btn-link danger" onClick={onDelete} disabled={busy}>
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

interface FormProps {
  account?: Account;
  onCancel: () => void;
  onSave: (input: {
    name: string;
    type: AccountType;
    openingBalancePaise: number;
    openingBalanceDate: string;
    minBalancePaise: number | null;
  }) => void;
}

function AccountForm({ account, onCancel, onSave }: FormProps) {
  const liability = account ? isLiability(account.type) : false;
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState<AccountType>(account?.type ?? 'bank');
  const [opening, setOpening] = useState(
    account
      ? String(
          Math.abs(account.openingBalancePaise) / 100,
        )
      : '0',
  );
  const [date, setDate] = useState(account?.openingBalanceDate ?? todayKey());
  const [floor, setFloor] = useState(
    account?.minBalancePaise != null ? String(account.minBalancePaise / 100) : '',
  );

  const parsed = parseAmount(opening);
  const invalid = name.trim() === '' || parsed === null;

  function submit() {
    if (invalid || parsed === null) return;
    const isLiabilityType = type === 'credit_card' || type === 'loan';
    onSave({
      name: name.trim(),
      type,
      // Entered as a positive figure and stored signed, so the person typing an
      // outstanding never has to think about the sign convention.
      openingBalancePaise: isLiabilityType ? -Math.abs(parsed) : parsed,
      openingBalanceDate: date,
      minBalancePaise: floor.trim() === '' ? null : parseAmount(floor),
    });
  }

  return (
    <section className="panel">
      <h2>{account ? `Edit ${account.name}` : 'New account'}</h2>

      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        {account && (
          <small className="muted">
            Renaming applies to all history — transactions store the id, not the name.
          </small>
        )}
      </label>

      <label className="field">
        <span>Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as AccountType)}
          disabled={Boolean(account)}
        >
          {(Object.keys(TYPE_LABELS) as AccountType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        {account && <small className="muted">Type cannot change after creation.</small>}
      </label>

      <label className="field">
        <span>{liability || type === 'credit_card' || type === 'loan' ? 'Opening outstanding' : 'Opening balance'}</span>
        <input value={opening} onChange={(e) => setOpening(e.target.value)} inputMode="decimal" />
        <small className={parsed === null ? 'error' : 'muted'}>
          {parsed === null ? 'Not a valid amount' : formatPaise(parsed)}
        </small>
      </label>

      <label className="field">
        <span>As at</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>

      {!(type === 'credit_card' || type === 'loan') && (
        <label className="field">
          <span>Minimum balance floor (optional)</span>
          <input value={floor} onChange={(e) => setFloor(e.target.value)} inputMode="decimal" />
          <small className="muted">A warning only — never blocks a save.</small>
        </label>
      )}

      <div className="row">
        <button type="button" className="btn btn-primary" onClick={submit} disabled={invalid}>
          Save
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
